import { StoreSubject } from "@/lib/store/StoreSubject";
import {
  createDocumentSession,
  liveSnapshotFromSession,
  sessionFromLiveSnapshot,
  type DocumentSession,
  type LiveEditorSnapshot,
} from "./documentSession";

export interface WorkspaceState {
  sessions: DocumentSession[];
  activeSessionId: string;
}

function sessionTabSignature(session: DocumentSession): string {
  const dirty = session.history.getState(session.document.revision).dirtyRevision > 0 ? "1" : "0";
  return `${session.id}|${session.path ?? ""}|${session.kind ?? "document"}|${session.document.name}|${session.document.revision}|${dirty}`;
}

function workspaceEqual(left: WorkspaceState, right: WorkspaceState): boolean {
  return left === right
    || (left.activeSessionId === right.activeSessionId
      && left.sessions.length === right.sessions.length
      && left.sessions.every((session, index) => {
        const other = right.sessions[index];
        return Boolean(other) && sessionTabSignature(session) === sessionTabSignature(other!);
      }));
}

export class WorkspaceStore {
  readonly state: StoreSubject<WorkspaceState>;

  constructor(initial?: DocumentSession) {
    const session = initial ?? createDocumentSession();
    this.state = new StoreSubject<WorkspaceState>({
      sessions: [session],
      activeSessionId: session.id,
    }, { equals: workspaceEqual });
  }

  getState = (): WorkspaceState => {
    return this.state.getValue();
  };

  getActiveSession = (): DocumentSession => {
    const { sessions, activeSessionId } = this.getState();
    const active = sessions.find(session => session.id === activeSessionId);
    if (!active) {
      throw new Error(`Active workspace session "${activeSessionId}" is missing.`);
    }
    return active;
  };

  findSessionByPath = (path: string): DocumentSession | undefined => {
    return this.getState().sessions.find(session => session.path === path);
  };

  findSessionByKind = (kind: NonNullable<DocumentSession["kind"]>): DocumentSession | undefined => {
    return this.getState().sessions.find(session => (session.kind ?? "document") === kind);
  };

  /** Replace the parked active session with the current live editor snapshot (no switch). */
  parkActive = (live: LiveEditorSnapshot): DocumentSession => {
    const current = this.getState();
    const sessions = current.sessions.map(session => {
      if (session.id !== current.activeSessionId) {
        return session;
      }
      return sessionFromLiveSnapshot(live, session.id);
    });
    const next = { ...current, sessions };
    this.state.next(next);
    return sessions.find(session => session.id === current.activeSessionId)!;
  };

  /**
   * Park the live editor into the current active session, then activate `sessionId`.
   * Returns the snapshot to hydrate into stores / history ref.
   */
  switchTo = (sessionId: string, live: LiveEditorSnapshot): LiveEditorSnapshot => {
    const current = this.getState();
    if (sessionId === current.activeSessionId) {
      this.parkActive(live);
      return live;
    }
    if (!current.sessions.some(session => session.id === sessionId)) {
      throw new Error(`Unknown workspace session "${sessionId}".`);
    }

    const parked = current.sessions.map(session => {
      if (session.id !== current.activeSessionId) {
        return session;
      }
      return sessionFromLiveSnapshot(live, session.id);
    });
    const next: WorkspaceState = {
      sessions: parked,
      activeSessionId: sessionId,
    };
    this.state.next(next);
    return liveSnapshotFromSession(parked.find(session => session.id === sessionId)!);
  };

  /** Add a session and make it active. Parks `live` into the previous active session first. */
  openSession = (session: DocumentSession, live: LiveEditorSnapshot): LiveEditorSnapshot => {
    const current = this.getState();
    const parked = current.sessions.map(existing => {
      if (existing.id !== current.activeSessionId) {
        return existing;
      }
      return sessionFromLiveSnapshot(live, existing.id);
    });
    this.state.next({
      sessions: [...parked, session],
      activeSessionId: session.id,
    });
    return liveSnapshotFromSession(session);
  };

  /**
   * Replace the active session in place (web-style New / Import).
   * Keeps the same session id so UI that keys on it stays stable for a single tab.
   */
  replaceActive = (session: DocumentSession, live: LiveEditorSnapshot): LiveEditorSnapshot => {
    const current = this.getState();
    const replacement = sessionFromLiveSnapshot(
      {
        document: session.document,
        history: session.history,
        selection: session.selection,
        tool: session.tool,
        viewport: session.viewport,
        path: session.path,
      },
      current.activeSessionId,
    );
    const sessions = current.sessions.map(existing => {
      if (existing.id !== current.activeSessionId) {
        return existing;
      }
      return replacement;
    });
    this.state.next({
      sessions,
      activeSessionId: current.activeSessionId,
    });
    return liveSnapshotFromSession(replacement);
  };

  /**
   * Close a session. If it was active, parks `live` first when closing a different tab is N/A;
   * when closing the active tab, `live` is discarded (caller should confirm dirty).
   * Always leaves at least one blank session.
   */
  closeSession = (sessionId: string, live: LiveEditorSnapshot): LiveEditorSnapshot => {
    return this.closeSessions([sessionId], live);
  };

  /**
   * Close many sessions in one shot. Always leaves at least one blank session.
   * Returns a snapshot to hydrate when the previously active session was closed (or replaced).
   */
  closeSessions = (sessionIds: readonly string[], live: LiveEditorSnapshot): LiveEditorSnapshot => {
    const toClose = new Set(sessionIds);
    if (toClose.size === 0) {
      return live;
    }

    const current = this.getState();
    const closingActive = toClose.has(current.activeSessionId);
    const closedActiveIndex = closingActive
      ? current.sessions.findIndex(session => session.id === current.activeSessionId)
      : -1;

    const remaining = current.sessions
      .map(session => {
        if (session.id !== current.activeSessionId || closingActive) {
          return session;
        }
        return sessionFromLiveSnapshot(live, session.id);
      })
      .filter(session => !toClose.has(session.id));

    if (remaining.length === 0) {
      const blank = createDocumentSession();
      this.state.next({ sessions: [blank], activeSessionId: blank.id });
      return liveSnapshotFromSession(blank);
    }

    if (!closingActive) {
      this.state.next({
        sessions: remaining,
        activeSessionId: current.activeSessionId,
      });
      return live;
    }

    const remainingIds = new Set(remaining.map(session => session.id));
    let nextActive: DocumentSession | undefined;
    for (let index = closedActiveIndex - 1; index >= 0; index -= 1) {
      const candidate = current.sessions[index];
      if (candidate && remainingIds.has(candidate.id)) {
        nextActive = remaining.find(session => session.id === candidate.id);
        break;
      }
    }
    if (!nextActive) {
      for (let index = closedActiveIndex + 1; index < current.sessions.length; index += 1) {
        const candidate = current.sessions[index];
        if (candidate && remainingIds.has(candidate.id)) {
          nextActive = remaining.find(session => session.id === candidate.id);
          break;
        }
      }
    }
    nextActive ??= remaining[0]!;

    this.state.next({
      sessions: remaining,
      activeSessionId: nextActive.id,
    });
    return liveSnapshotFromSession(nextActive);
  };

  /** Reorder sessions by moving `fromIndex` to `toIndex` (both clamped). Active id unchanged. */
  reorderSessions = (fromIndex: number, toIndex: number): void => {
    const current = this.getState();
    if (
      fromIndex === toIndex
      || fromIndex < 0
      || toIndex < 0
      || fromIndex >= current.sessions.length
      || toIndex >= current.sessions.length
    ) {
      return;
    }
    const sessions = current.sessions.slice();
    const [moved] = sessions.splice(fromIndex, 1);
    sessions.splice(toIndex, 0, moved!);
    this.state.next({ ...current, sessions });
  };

  setActivePath = (path: string | undefined): void => {
    const current = this.getState();
    const sessions = current.sessions.map(session => {
      if (session.id !== current.activeSessionId) {
        return session;
      }
      return { ...session, path };
    });
    this.state.next({ ...current, sessions });
  };
}

export const workspaceStore = new WorkspaceStore();
